import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SoundtrackPanel({ axisZones = [] }) {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [zones, setZones] = useState([]);
  const [associations, setAssociations] = useState({});
  const [nowPlaying, setNowPlaying] = useState({});
  const [volumes, setVolumes] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState('');
  const [playbackStates, setPlaybackStates] = useState({});

  const loadNowPlaying = useCallback(async (zoneId) => {
    try {
      const response = await axios.get(
        `${API}/soundtrack/zones/${encodeURIComponent(zoneId)}/now-playing`
      );

      setNowPlaying(prev => ({
        ...prev,
        [zoneId]: response.data.now_playing || null
      }));

      const playback = response.data.playback || {};

      if (playback.state) {
        setPlaybackStates(prev => ({
          ...prev,
          [zoneId]: String(playback.state).toLowerCase()
        }));
      }

      if (playback.volume !== undefined && playback.volume !== null) {
        const percent = Math.round(Number(playback.volume) * 100 / 16);
        setVolumes(prev => ({
          ...prev,
          [zoneId]: percent
        }));
      }
    } catch (err) {
      console.error("Now playing error:", err);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [
        statusRes,
        accountsRes,
        locationsRes,
        zonesRes,
        associationsRes
      ] = await Promise.all([
        axios.get(`${API}/soundtrack/status`),
        axios.get(`${API}/soundtrack/accounts`),
        axios.get(`${API}/soundtrack/locations`),
        axios.get(`${API}/soundtrack/zones`),
        axios.get(`${API}/soundtrack/associations`)
      ]);

      setStatus(statusRes.data);
      setAccounts(accountsRes.data.accounts || []);
      setLocations(locationsRes.data.locations || []);

      const soundtrackZones = zonesRes.data.zones || [];
      setZones(soundtrackZones);

      const associationMap = {};
      for (const item of associationsRes.data.associations || []) {
        associationMap[item.soundtrack_zone_id] = item.axis_zone_id;
      }
      setAssociations(associationMap);

      await Promise.all(
        soundtrackZones.map(zone => loadNowPlaying(zone.id))
      );
    } catch (err) {
      console.error('Soundtrack loading error:', err);
      setError(
        err?.response?.data?.detail ||
        'Impossible de charger les informations Soundtrack'
      );
    } finally {
      setLoading(false);
    }
  }, [loadNowPlaying]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!zones.length) {
      return;
    }

    const interval = setInterval(() => {
      zones.forEach(zone => {
        loadNowPlaying(zone.id);
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [zones, loadNowPlaying]);

  const runAction = async (zoneId, action) => {
    setBusy(prev => ({ ...prev, [zoneId]: true }));

    try {
      await axios.post(
        `${API}/soundtrack/zones/${encodeURIComponent(zoneId)}/${action}`
      );

      setPlaybackStates(prev => ({
        ...prev,
        [zoneId]: action === "pause" ? "paused" : "playing"
      }));
      setTimeout(() => loadNowPlaying(zoneId), 800);
    } catch (err) {
      alert(
        err?.response?.data?.detail ||
        `Échec de l'action ${action}`
      );
    } finally {
      setBusy(prev => ({ ...prev, [zoneId]: false }));
    }
  };

  const setZoneVolume = async (zoneId, percent) => {
    setVolumes(prev => ({
      ...prev,
      [zoneId]: percent
    }));

    try {
      await axios.put(
        `${API}/soundtrack/zones/${encodeURIComponent(zoneId)}/volume`,
        { volume: percent }
      );
    } catch (err) {
      alert(
        err?.response?.data?.detail ||
        'Échec du réglage du volume'
      );
    }
  };

  const associateZone = async (zone, axisZoneId) => {
    try {
      if (!axisZoneId) {
        await axios.delete(
          `${API}/soundtrack/associations/${encodeURIComponent(zone.id)}`
        );

        setAssociations(prev => {
          const next = { ...prev };
          delete next[zone.id];
          return next;
        });

        return;
      }

      const axisZone = axisZones.find(
        item => String(item.id) === String(axisZoneId)
      );

      await axios.put(
        `${API}/soundtrack/associations/${encodeURIComponent(zone.id)}`,
        {
          axis_zone_id: String(axisZoneId),
          soundtrack_zone_name: zone.name || '',
          axis_zone_name: axisZone?.name || ''
        }
      );

      setAssociations(prev => ({
        ...prev,
        [zone.id]: String(axisZoneId)
      }));
    } catch (err) {
      alert(
        err?.response?.data?.detail ||
        'Échec de l’association avec la zone Axis'
      );
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-300">
          Chargement de Soundtrack...
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold">Soundtrack</h2>
          <p className="text-sm text-gray-400 mt-1">
            Soundtrack Your Brand / Axis Audio Manager Pro
          </p>
        </div>

        <button
          type="button"
          onClick={refresh}
          className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded font-medium"
        >
          Actualiser
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-600 border border-red-500 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gray-800 rounded-lg p-5">
          <div className="text-sm text-gray-400 mb-1">Connexion API</div>
          <div className={
            status?.connected
              ? 'text-green-400 font-semibold'
              : 'text-red-400 font-semibold'
          }>
            {status?.connected ? 'Connectée' : 'Déconnectée'}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-5">
          <div className="text-sm text-gray-400">Comptes</div>
          <div className="text-2xl font-bold mt-1">{accounts.length}</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-5">
          <div className="text-sm text-gray-400">Emplacements</div>
          <div className="text-2xl font-bold mt-1">{locations.length}</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-5">
          <div className="text-sm text-gray-400">Zones Soundtrack</div>
          <div className="text-2xl font-bold mt-1">{zones.length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {zones.map(zone => {
          const playing = nowPlaying[zone.id];
          const track = playing?.track;
          const artists = track?.artists || [];
          const image = track?.album?.image?.url;
          const volume = volumes[zone.id] ?? 50;

          return (
            <div
              key={zone.id}
              className="bg-gray-800 border border-gray-700 rounded-lg p-6"
            >
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h3 className="text-xl font-semibold text-green-400">
                    {zone.name}
                  </h3>

                  <div className="text-sm text-gray-400 mt-1">
                    {zone.location_name || 'Emplacement inconnu'}
                  </div>

                  {zone.account_name && (
                    <div className="text-xs text-gray-500 mt-1">
                      {zone.account_name}
                    </div>
                  )}
                </div>

                <span className="px-2 py-1 rounded text-xs bg-green-700">
                  Soundtrack
                </span>
              </div>

              <div className="flex gap-4 mb-5">
                {image ? (
                  <img
                    src={image}
                    alt=""
                    className="w-20 h-20 rounded object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded bg-gray-700 flex items-center justify-center text-3xl">
                    ♪
                  </div>
                )}

                <div className="min-w-0">
                  <div className="text-xs uppercase text-gray-500 mb-1">
                    {playbackStates[zone.id] === "paused" ? "EN PAUSE" : "EN COURS"}
                  </div>

                  <div className="font-semibold truncate">
                    {track?.name || 'Aucun titre détecté'}
                  </div>

                  <div className="text-sm text-gray-400 truncate">
                    {artists.length
                      ? artists.map(a => a.name).join(', ')
                      : '—'}
                  </div>

                  {track?.album?.name && (
                    <div className="text-xs text-gray-500 mt-1 truncate">
                      {track.album.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-5">
                <button
                  type="button"
                  disabled={busy[zone.id]}
                  onClick={() => runAction(zone.id, 'play')}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 px-3 py-2 rounded"
                >
                  ▶ Play
                </button>

                <button
                  type="button"
                  disabled={busy[zone.id]}
                  onClick={() => runAction(zone.id, 'pause')}
                  className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 px-3 py-2 rounded"
                >
                  ⏸ Pause
                </button>

                <button
                  type="button"
                  disabled={busy[zone.id]}
                  onClick={() => runAction(zone.id, 'skip')}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-3 py-2 rounded"
                >
                  ⏭ Suivant
                </button>
              </div>

              <div className="mb-5">
                <div className="flex justify-between text-sm mb-2">
                  <span>Volume</span>
                  <span>{volume}%</span>
                </div>

                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={volume}
                  onChange={e =>
                    setVolumes(prev => ({
                      ...prev,
                      [zone.id]: Number(e.target.value)
                    }))
                  }
                  onMouseUp={e =>
                    setZoneVolume(zone.id, Number(e.target.value))
                  }
                  onTouchEnd={e =>
                    setZoneVolume(zone.id, Number(e.currentTarget.value))
                  }
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Zone Axis associée
                </label>

                <select
                  value={associations[zone.id] || ''}
                  onChange={e => associateZone(zone, e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
                >
                  <option value="">Aucune association</option>

                  {axisZones.map(axisZone => (
                    <option
                      key={axisZone.id}
                      value={axisZone.id}
                    >
                      {axisZone.name || `Zone Axis ${axisZone.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>

      {zones.length === 0 && (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
          Aucune zone Soundtrack disponible.
        </div>
      )}
    </div>
  );
}
